import { ImportRequestScanResult } from 'services/import-requests/enums'
import type { ImportRequestObjectCounts } from 'services/import-requests/types'
import { formatDate } from 'utils/date'

export const IMPORT_REQUEST_SCAN_DETAILS_TITLE = 'Data import request scan'

export const IMPORT_REQUEST_SCAN_RESULT_MESSAGE: Record<ImportRequestScanResult, string> = {
  [ImportRequestScanResult.NotStarted]: 'Not started',
  [ImportRequestScanResult.InProgress]: 'In progress...',
  [ImportRequestScanResult.Clean]: 'No threats found',
  [ImportRequestScanResult.ThreatDetected]: 'Malware threat detected. This request was automatically rejected.',
  [ImportRequestScanResult.ScanFailed]:
    'The scan could not complete. Review the request manually before approving or rejecting.'
}

export const IMPORT_REQUEST_MIXED_SCAN_RESULT_MESSAGE =
  'Threats were detected in some objects. Only clean objects can be released after approval.'

export const getImportRequestScanResultMessage = (
  scanResult: ImportRequestScanResult,
  counts?: Pick<ImportRequestObjectCounts, 'clean' | 'threatDetected'>
): string => {
  if (
    scanResult === ImportRequestScanResult.Clean &&
    counts !== undefined &&
    counts.clean > 0 &&
    counts.threatDetected > 0
  ) {
    return IMPORT_REQUEST_MIXED_SCAN_RESULT_MESSAGE
  }

  return IMPORT_REQUEST_SCAN_RESULT_MESSAGE[scanResult]
}

export const resolveImportRequestScanResult = (
  scanResult?: ImportRequestScanResult,
  isLoading?: boolean
): ImportRequestScanResult => {
  if (isLoading || scanResult === undefined) {
    return ImportRequestScanResult.InProgress
  }

  return scanResult
}

export const shouldShowImportRequestScanMetadata = (scanResult: ImportRequestScanResult): boolean =>
  ![ImportRequestScanResult.ThreatDetected, ImportRequestScanResult.NotStarted].includes(scanResult)

interface UseImportRequestScanDetailsParams {
  scanResult?: ImportRequestScanResult
  scanStartedAt?: string
  scanCompletedAt?: string
  scanStatusCode?: string
  counts?: ImportRequestObjectCounts
  formattedTotalSize?: string
  isLoading?: boolean
}

export const useImportRequestScanDetails = ({
  scanResult,
  scanStartedAt,
  scanCompletedAt,
  scanStatusCode,
  counts,
  formattedTotalSize,
  isLoading
}: UseImportRequestScanDetailsParams) => {
  const resolvedScanResult = resolveImportRequestScanResult(scanResult, isLoading)

  return {
    title: IMPORT_REQUEST_SCAN_DETAILS_TITLE,
    resolvedScanResult,
    scanResultMessage: getImportRequestScanResultMessage(resolvedScanResult, counts),
    formattedScanStartedAt: scanStartedAt ? formatDate(scanStartedAt) : 'N/A',
    formattedScanCompletedAt: scanCompletedAt ? formatDate(scanCompletedAt) : 'N/A',
    formattedScanStatusCode: scanStatusCode ? scanStatusCode : 'N/A',
    shouldShowScanMetadata: shouldShowImportRequestScanMetadata(resolvedScanResult),
    shouldShowScanCounts: counts !== undefined,
    shouldShowTotalSize: formattedTotalSize !== undefined && formattedTotalSize !== '',
    scanCounts: counts,
    formattedTotalSize
  }
}
